import { API_URL } from "../constants/constants"

/**
 * Единый помощник для POST-запросов к API турниров.
 * Инкапсулирует fetch, JSON-парсинг и обработку ошибок сети.
 *
 * @param {string} path - Путь относительно API_URL (например, "/tournaments/").
 * @param {object} body - Тело запроса (будет сериализовано в JSON).
 * @param {object} [options] - Доп. опции: { signal } для AbortController.
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
const postJson = async (path, body, { signal } = {}) => {
  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal,
    })

    if (!response.ok) {
      return {
        success: false,
        error: `Request failed with status ${response.status}`,
      }
    }

    const data = await response.json()

    if (!data.success) {
      return { success: false, error: data.error || "Request was unsuccessful" }
    }

    return { success: true, data }
  } catch (error) {
    // AbortError не считаем реальной ошибкой — запрос был отменён намеренно
    if (error?.name === "AbortError") {
      return { success: false, aborted: true }
    }

    console.error(`Tournaments API error [${path}]:`, error)
    return { success: false, error: error?.message || String(error) }
  }
}

/**
 * Список активных турниров.
 * @param {string} [userWallet]
 * @param {object} [options]
 * @returns {Promise<{ success: boolean, tournaments?: array, error?: string }>}
 */
export const getTournaments = async (userWallet, options) => {
  const result = await postJson(
    "/tournaments/",
    { user_wallet: userWallet || "" },
    options,
  )

  if (!result.success) {
    return {
      success: false,
      tournaments: [],
      error: result.error,
      aborted: result.aborted,
    }
  }

  return { success: true, tournaments: result.data.tournaments || [] }
}

/**
 * Детали конкретного турнира вместе с призами.
 * @param {string|number} tournamentId
 * @param {string} [userWallet]
 * @param {object} [options]
 * @returns {Promise<{ success: boolean, tournament?: object, prizes?: array, error?: string }>}
 */
export const getTournamentDetails = async (
  tournamentId,
  userWallet,
  options,
) => {
  const result = await postJson(
    `/tournaments/details/${tournamentId}`,
    { user_wallet: userWallet || "" },
    options,
  )

  if (!result.success) {
    return {
      success: false,
      tournament: null,
      prizes: null,
      error: result.error,
      aborted: result.aborted,
    }
  }

  return {
    success: true,
    tournament: result.data.tournament || null,
    prizes: result.data.prizes || null,
  }
}

/**
 * История завершённых турниров пользователя.
 * @param {string} [userWallet]
 * @param {object} [options]
 * @returns {Promise<{ success: boolean, tournaments?: array, error?: string }>}
 */
export const getTournamentsHistory = async (userWallet, options) => {
  const result = await postJson(
    "/tournaments/history/",
    { user_wallet: userWallet || "" },
    options,
  )

  if (!result.success) {
    return { success: false, tournaments: [], error: result.error }
  }

  return { success: true, tournaments: result.data.tournaments || [] }
}

/**
 * Таблица лидеров турнира.
 * @param {string|number} tournamentId
 * @param {object} [params] - { userWallet, search }
 * @param {object} [options]
 * @returns {Promise<{ success: boolean, leaderboard?: array, error?: string }>}
 */
export const getLeaderboard = async (
  tournamentId,
  { userWallet, search = "" } = {},
  options,
) => {
  const result = await postJson(
    `/tournaments/leaderboard/${tournamentId}`,
    { user_wallet: userWallet || "", search },
    options,
  )

  if (!result.success) {
    return { success: false, leaderboard: [], error: result.error }
  }

  return { success: true, leaderboard: result.data.leaderboard || [] }
}
